/**
 * Proffeo Design System — editable token registry for the DS playground token editor.
 * Default values mirror proffeo-tokens.css (literal values only; var() aliases omitted).
 */

export type ProffeoTokenInputType = 'color' | 'length' | 'number' | 'text';
export type ProffeoTokenValueType = ProffeoTokenInputType;
export type ProffeoTokenPreviewType =
  'color' | 'typography' | 'spacing' | 'radius' | 'shadow' | 'density' | 'icon' | 'layout' | 'none';

export type ProffeoTokenEditorGroup =
  | 'baseColor'
  | 'colorVariant'
  | 'action'
  | 'button'
  | 'surface'
  | 'border'
  | 'text'
  | 'feedback'
  | 'focus'
  | 'status'
  | 'priority'
  | 'reportStatus'
  | 'domain'
  | 'typography'
  | 'radius'
  | 'shadow'
  | 'spacing'
  | 'table'
  | 'panel'
  | 'layout'
  | 'breakpoint'
  | 'form'
  | 'datepicker'
  | 'toolbar'
  | 'dialog'
  | 'density'
  | 'disabled'
  | 'state'
  | 'icon'
  | 'scrollbar';

export interface ProffeoTokenEditorField {
  name: string;
  cssVariable: string;
  label: string;
  group: ProffeoTokenEditorGroup;
  category: ProffeoTokenEditorGroup;
  inputType: ProffeoTokenInputType;
  valueType: ProffeoTokenValueType;
  defaultValue: string;
  currentValue: string;
  description: string;
  usage: string;
  legacyAliases: string[];
  editable: boolean;
  previewType: ProffeoTokenPreviewType;
}

export const PROFFEO_TOKEN_EDITOR_GROUP_LABELS: Record<ProffeoTokenEditorGroup, string> = {
  baseColor: 'Seed colors (3 layers)',
  colorVariant: 'Palettes (derived)',
  action: 'Action colors',
  button: 'Buttons',
  surface: 'Surfaces',
  border: 'Borders',
  text: 'Text colors',
  feedback: 'Feedback colors',
  focus: 'Focus',
  status: 'Task status',
  priority: 'Task priority',
  reportStatus: 'Report status',
  domain: 'Domain exceptions',
  typography: 'Typography',
  radius: 'Border radius',
  shadow: 'Shadows',
  spacing: 'Spacing',
  table: 'Table / grid',
  panel: 'Panel / workspace',
  layout: 'Application shell',
  breakpoint: 'Breakpoints',
  form: 'Form',
  datepicker: 'Datepicker',
  toolbar: 'Toolbar',
  dialog: 'Dialog',
  density: 'Density',
  disabled: 'Disabled / readonly',
  state: 'Interaction states',
  icon: 'Icon sizes',
  scrollbar: 'Scrollbar'
};

export const PROFFEO_TOKEN_EDITOR_GROUP_ORDER: ProffeoTokenEditorGroup[] = [
  'typography',
  'baseColor',
  'colorVariant',
  'action',
  'button',
  'surface',
  'border',
  'text',
  'feedback',
  'focus',
  'status',
  'priority',
  'reportStatus',
  'domain',
  'radius',
  'shadow',
  'spacing',
  'table',
  'panel',
  'layout',
  'breakpoint',
  'form',
  'datepicker',
  'toolbar',
  'dialog',
  'density',
  'disabled',
  'state',
  'icon',
  'scrollbar'
];

const LEGACY_ALIASES: Record<string, string[]> = {
  '--proffeo-seed-accent': ['--proffeo-color-action-accent', '--color-proffeo-accent'],
  '--proffeo-seed-primary': ['--proffeo-color-surface-on'],
  '--proffeo-color-action-accent': ['--color-proffeo-accent'],
  '--proffeo-color-action-primary': ['--color-proffeo-action-primary'],
  '--proffeo-color-text': ['--color-proffeo-font', '--color-proffeo-default'],
  '--proffeo-color-text-muted': ['--color-proffeo-muted'],
  '--proffeo-color-text-disabled': ['--color-proffeo-disabled'],
  '--proffeo-color-text-on-action': ['--color-proffeo-on-action'],
  '--proffeo-color-danger': ['--color-proffeo-danger'],
  '--proffeo-color-warning': ['--color-proffeo-warning'],
  '--proffeo-color-success': ['--color-proffeo-success'],
  '--proffeo-color-info': ['--color-proffeo-info'],
  '--proffeo-status-in-planning': ['--color-proffeo-in-planning'],
  '--proffeo-status-to-do': ['--color-proffeo-to-do'],
  '--proffeo-status-in-progress': ['--color-proffeo-in-progress'],
  '--proffeo-status-in-review': ['--color-proffeo-in-review'],
  '--proffeo-status-accepted': ['--color-proffeo-accepted'],
  '--proffeo-status-rejected': ['--color-proffeo-rejected'],
  '--proffeo-status-prod-ready': ['--color-proffeo-prod-ready'],
  '--proffeo-status-done': ['--color-proffeo-done'],
  '--proffeo-priority-normal': ['--color-proffeo-priority-normal'],
  '--proffeo-priority-medium': ['--color-proffeo-priority-medium'],
  '--proffeo-priority-high': ['--color-proffeo-priority-high'],
  '--proffeo-border-width': ['--border-width'],
  '--proffeo-radius-md': ['--radius-md'],
  '--proffeo-ui-form-field-height': ['--material-container-height'],
  '--proffeo-layout-content-max-width': ['--max-width-content'],
  '--proffeo-breakpoint-sm': ['--breakpoint-sm'],
  '--proffeo-breakpoint-md': ['--breakpoint-md'],
  '--proffeo-breakpoint-lg': ['--breakpoint-lg'],
  '--proffeo-breakpoint-xl': ['--breakpoint-xl'],
  '--proffeo-breakpoint-2xl': ['--breakpoint-2xl']
};

function sentenceFromLabel(label: string): string {
  return label.charAt(0).toUpperCase() + label.slice(1);
}

function previewTypeFor(group: ProffeoTokenEditorGroup, inputType: ProffeoTokenInputType): ProffeoTokenPreviewType {
  if (
    group === 'action' ||
    group === 'baseColor' ||
    group === 'colorVariant' ||
    group === 'button' ||
    group === 'surface' ||
    group === 'border' ||
    group === 'text' ||
    group === 'feedback' ||
    group === 'status' ||
    group === 'priority' ||
    group === 'reportStatus' ||
    group === 'scrollbar'
  ) {
    return 'color';
  }

  if (group === 'typography') {
    return 'typography';
  }

  if (group === 'spacing') {
    return 'spacing';
  }

  if (group === 'radius') {
    return 'radius';
  }

  if (group === 'shadow') {
    return 'shadow';
  }

  if (group === 'density') {
    return 'density';
  }

  if (group === 'icon') {
    return 'icon';
  }

  if (group === 'layout' || group === 'breakpoint' || group === 'panel') {
    return 'layout';
  }

  return inputType === 'color' ? 'color' : 'none';
}

function field(
  group: ProffeoTokenEditorGroup,
  cssVariable: string,
  label: string,
  defaultValue: string,
  inputType: ProffeoTokenInputType,
  editable = true
): ProffeoTokenEditorField {
  const description = `${sentenceFromLabel(label)} token in the ${PROFFEO_TOKEN_EDITOR_GROUP_LABELS[group].toLowerCase()} public contract.`;
  return {
    name: cssVariable.replace(/^--/, ''),
    group,
    category: group,
    cssVariable,
    label,
    defaultValue,
    currentValue: defaultValue,
    inputType,
    valueType: inputType,
    description,
    usage: `Use ${cssVariable} for ${label.toLowerCase()} across Proffeo UI, Material adapters, and Tailwind aliases.`,
    legacyAliases: LEGACY_ALIASES[cssVariable] ?? [],
    editable,
    previewType: previewTypeFor(group, inputType)
  };
}

function hexColor(
  group: ProffeoTokenEditorGroup,
  cssVariable: string,
  label: string,
  defaultValue: string
): ProffeoTokenEditorField {
  return field(group, cssVariable, label, defaultValue, 'color');
}

function rgbaColor(
  group: ProffeoTokenEditorGroup,
  cssVariable: string,
  label: string,
  defaultValue: string
): ProffeoTokenEditorField {
  return field(group, cssVariable, label, defaultValue, 'text');
}

function length(
  group: ProffeoTokenEditorGroup,
  cssVariable: string,
  label: string,
  defaultValue: string
): ProffeoTokenEditorField {
  return field(group, cssVariable, label, defaultValue, 'length');
}

function num(
  group: ProffeoTokenEditorGroup,
  cssVariable: string,
  label: string,
  defaultValue: string
): ProffeoTokenEditorField {
  return field(group, cssVariable, label, defaultValue, 'number');
}

function text(
  group: ProffeoTokenEditorGroup,
  cssVariable: string,
  label: string,
  defaultValue: string
): ProffeoTokenEditorField {
  return field(group, cssVariable, label, defaultValue, 'text');
}

function derivedColor(
  group: ProffeoTokenEditorGroup,
  cssVariable: string,
  label: string,
  defaultValue: string,
  source: string
): ProffeoTokenEditorField {
  return {
    ...field(group, cssVariable, label, defaultValue, 'text', false),
    description: `${sentenceFromLabel(label)} token derived from ${source}.`,
    usage: `Change ${source} to update ${cssVariable}; this derivative token is read-only in the playground.`
  };
}

/** All editable and derived tokens — values mirror proffeo-tokens.css. */
export const PROFFEO_TOKEN_EDITOR_FIELDS: ProffeoTokenEditorField[] = [
  // Typography
  {
    ...length('typography', '--proffeo-base-size', 'Base size (1rem)', '16px'),
    description: 'Root font size on html; sets the computed pixel value of 1rem for the entire app.',
    usage: 'Applied via html { font-size: var(--proffeo-base-size) }. Scales all rem-based typography tokens.'
  },
  text('typography', '--proffeo-font-family', 'Font family', "Geist, 'Helvetica Neue', sans-serif"),
  length('typography', '--proffeo-font-size-xs', 'Font size XS', '0.75rem'),
  length('typography', '--proffeo-font-size-sm', 'Font size SM', '0.875rem'),
  length('typography', '--proffeo-font-size-base', 'Font size base', '1rem'),
  length('typography', '--proffeo-text-body-size', 'Body text size', '1rem'),
  length('typography', '--proffeo-font-size-lg', 'Font size LG', '1.125rem'),
  length('typography', '--proffeo-font-size-xl', 'Font size XL', '1.25rem'),
  length('typography', '--proffeo-font-size-2xl', 'Font size 2XL', '1.5rem'),
  length('typography', '--proffeo-font-size-3xl', 'Font size 3XL', '1.875rem'),
  length('typography', '--proffeo-font-size-4xl', 'Font size 4XL', '2.25rem'),
  num('typography', '--proffeo-font-weight-thin', 'Weight normal', '400'),
  num('typography', '--proffeo-font-weight-normal', 'Weight medium', '500'),
  num('typography', '--proffeo-font-weight-semibold', 'Weight semibold', '600'),
  num('typography', '--proffeo-font-weight-bold', 'Weight bold', '700'),
  num('typography', '--proffeo-line-height-tight', 'Line height tight', '1.25'),
  num('typography', '--proffeo-line-height-normal', 'Line height normal', '1.5'),
  length('typography', '--proffeo-letter-spacing-table-header', 'Letter spacing table header', '0.06em'),

  // Seed colors — the 3 responsibility layers
  {
    ...hexColor('baseColor', '--proffeo-seed-base', 'Seed base (texts, icons, borders)', '#1e293b'),
    description: 'Seed of the base layer: texts, icons, dividers, borders, shadows, scrims.',
    usage: 'Drives the base palette (--proffeo-color-base-50..950 and alpha steps).'
  },
  {
    ...hexColor('baseColor', '--proffeo-seed-accent', 'Seed accent (CTA)', '#d5203e'),
    description: 'Seed of the accent layer: most important, eye-catching call-to-action buttons and their states.',
    usage: 'Drives the accent palette (--proffeo-color-accent-100..700 and alpha steps).'
  },
  {
    ...hexColor('baseColor', '--proffeo-seed-primary', 'Seed primary (controls + backgrounds)', '#526e96'),
    description:
      "Seed of the primary layer: the app's dominant color — form controls (checkbox, radio, sliders, toggles), second-tier buttons, AND application/container backgrounds. Controls and the surfaces they sit on share one seed so they always stay in sync.",
    usage: 'Drives the primary palette (--proffeo-color-primary-0..700 and alpha steps).'
  },

  // Palettes — steps derived from the seeds (read-only)
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-50',
    'Base 50 (near-white)',
    'color-mix(in srgb, var(--proffeo-seed-base) 3%, #ffffff)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-100',
    'Base 100',
    'color-mix(in srgb, var(--proffeo-seed-base) 10%, #ffffff)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-200',
    'Base 200 (border)',
    'color-mix(in srgb, var(--proffeo-seed-base) 20%, #ffffff)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-300',
    'Base 300',
    'color-mix(in srgb, var(--proffeo-seed-base) 30%, #ffffff)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-400',
    'Base 400 (disabled text)',
    'color-mix(in srgb, var(--proffeo-seed-base) 45%, #ffffff)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-500',
    'Base 500 (muted text)',
    'color-mix(in srgb, var(--proffeo-seed-base) 68%, #ffffff)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-700',
    'Base 700 (hover)',
    'color-mix(in srgb, var(--proffeo-seed-base) 85%, #ffffff)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-800',
    'Base 800 (text)',
    'color-mix(in srgb, var(--proffeo-seed-base) 90%, #ffffff)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-900',
    'Base 900 (seed / icon)',
    'var(--proffeo-seed-base)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-950',
    'Base 950 (inverse shell)',
    'color-mix(in srgb, var(--proffeo-seed-base) 76%, #000000)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-alpha-6',
    'Base alpha 6 (hover wash)',
    'color-mix(in srgb, var(--proffeo-seed-base) 6%, transparent)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-alpha-10',
    'Base alpha 10 (subtle scrim)',
    'color-mix(in srgb, var(--proffeo-seed-base) 10%, transparent)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-alpha-12',
    'Base alpha 12 (dropdown shadow)',
    'color-mix(in srgb, var(--proffeo-seed-base) 12%, transparent)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-alpha-18',
    'Base alpha 18 (dialog shadow)',
    'color-mix(in srgb, var(--proffeo-seed-base) 18%, transparent)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-alpha-30',
    'Base alpha 30 (scrim)',
    'color-mix(in srgb, var(--proffeo-seed-base) 30%, transparent)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-alpha-50',
    'Base alpha 50 (strong scrim)',
    'color-mix(in srgb, var(--proffeo-seed-base) 50%, transparent)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-base-alpha-75',
    'Base alpha 75 (viewer scrim)',
    'color-mix(in srgb, var(--proffeo-seed-base) 75%, transparent)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-accent-100',
    'Accent 100 (tint)',
    'color-mix(in srgb, var(--proffeo-seed-accent) 12%, #ffffff)',
    '--proffeo-seed-accent'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-accent-300',
    'Accent 300 (disabled)',
    'color-mix(in srgb, var(--proffeo-seed-accent) 45%, #ffffff)',
    '--proffeo-seed-accent'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-accent-500',
    'Accent 500 (seed / CTA)',
    'var(--proffeo-seed-accent)',
    '--proffeo-seed-accent'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-accent-600',
    'Accent 600 (hover)',
    'color-mix(in srgb, var(--proffeo-seed-accent) 85%, #000000)',
    '--proffeo-seed-accent'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-accent-700',
    'Accent 700 (active)',
    'color-mix(in srgb, var(--proffeo-seed-accent) 70%, #000000)',
    '--proffeo-seed-accent'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-accent-alpha-12',
    'Accent alpha 12 (wash)',
    'color-mix(in srgb, var(--proffeo-seed-accent) 12%, transparent)',
    '--proffeo-seed-accent'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-accent-alpha-35',
    'Accent alpha 35 (focus ring)',
    'color-mix(in srgb, var(--proffeo-seed-accent) 35%, transparent)',
    '--proffeo-seed-accent'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-primary-0',
    'Primary 0 (cards)',
    'color-mix(in srgb, var(--proffeo-seed-primary) 0%, #ffffff)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-primary-50',
    'Primary 50',
    'color-mix(in srgb, var(--proffeo-seed-primary) 5%, #ffffff)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-primary-100',
    'Primary 100 (page)',
    'color-mix(in srgb, var(--proffeo-seed-primary) 10%, #ffffff)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-primary-200',
    'Primary 200 (subtle / track)',
    'color-mix(in srgb, var(--proffeo-seed-primary) 20%, #ffffff)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-primary-300',
    'Primary 300 (toolbar / toggle off)',
    'color-mix(in srgb, var(--proffeo-seed-primary) 40%, #ffffff)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-primary-400',
    'Primary 400 (toggle hover)',
    'color-mix(in srgb, var(--proffeo-seed-primary) 70%, #ffffff)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-primary-500',
    'Primary 500 (seed / control on)',
    'var(--proffeo-seed-primary)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-primary-600',
    'Primary 600 (hover)',
    'color-mix(in srgb, var(--proffeo-seed-primary) 85%, #000000)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-primary-700',
    'Primary 700 (active)',
    'color-mix(in srgb, var(--proffeo-seed-primary) 73%, #000000)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'colorVariant',
    '--proffeo-color-primary-alpha-10',
    'Primary alpha 10 (hover wash)',
    'color-mix(in srgb, var(--proffeo-seed-primary) 10%, transparent)',
    '--proffeo-seed-primary'
  ),

  // Action
  derivedColor(
    'action',
    '--proffeo-color-action-accent',
    'Accent',
    'var(--proffeo-color-accent-500)',
    '--proffeo-seed-accent'
  ),
  derivedColor(
    'action',
    '--proffeo-color-action-accent-hover',
    'Accent hover',
    'var(--proffeo-color-accent-600)',
    '--proffeo-seed-accent'
  ),
  derivedColor(
    'action',
    '--proffeo-color-action-accent-active',
    'Accent active',
    'var(--proffeo-color-accent-700)',
    '--proffeo-seed-accent'
  ),
  derivedColor(
    'action',
    '--proffeo-color-action-accent-disabled',
    'Accent disabled',
    'var(--proffeo-color-accent-300)',
    '--proffeo-seed-accent'
  ),
  derivedColor(
    'action',
    '--proffeo-color-action-primary',
    'Primary',
    'var(--proffeo-color-primary-500)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'action',
    '--proffeo-color-action-primary-hover',
    'Primary hover',
    'var(--proffeo-color-primary-600)',
    '--proffeo-seed-primary'
  ),

  // Surface
  derivedColor('surface', '--proffeo-color-surface', 'Surface', 'var(--proffeo-color-base-50)', '--proffeo-seed-base'),
  derivedColor(
    'surface',
    '--proffeo-color-surface-page',
    'Surface page',
    'var(--proffeo-color-base-100)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'surface',
    '--proffeo-color-surface-muted',
    'Surface muted',
    'var(--proffeo-color-base-100)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'surface',
    '--proffeo-color-surface-canvas',
    'Surface canvas',
    'color-mix(in srgb, var(--proffeo-seed-primary) 6%, var(--proffeo-color-white))',
    '--proffeo-seed-primary'
  ),
  {
    ...derivedColor(
      'surface',
      '--proffeo-color-surface-panel',
      'Surface panel',
      'var(--proffeo-color-white)',
      '--proffeo-color-white'
    ),
    usage: 'Fixed white; --proffeo-color-white is not a seed, so this derivative token is read-only in the playground.'
  },
  derivedColor(
    'surface',
    '--proffeo-color-surface-inset',
    'Surface inset',
    'var(--proffeo-color-primary-50)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'surface',
    '--proffeo-color-surface-emphasis',
    'Surface emphasis',
    'var(--proffeo-color-primary-700)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'surface',
    '--proffeo-color-surface-subtle',
    'Surface subtle',
    'var(--proffeo-color-base-200)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'surface',
    '--proffeo-color-surface-selected',
    'Surface selected',
    'var(--proffeo-color-base-100)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'surface',
    '--proffeo-color-surface-off',
    'Surface off (toggle)',
    'var(--proffeo-color-primary-300)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'surface',
    '--proffeo-color-surface-off-hover',
    'Surface off hover (toggle)',
    'var(--proffeo-color-primary-400)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'surface',
    '--proffeo-color-surface-on',
    'Surface on (toggle)',
    'var(--proffeo-color-primary-500)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'surface',
    '--proffeo-color-surface-hover',
    'Surface hover',
    'var(--proffeo-color-base-alpha-6)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'surface',
    '--proffeo-color-surface-disabled',
    'Surface disabled',
    'var(--proffeo-color-surface-page)',
    '--proffeo-seed-primary'
  ),
  derivedColor(
    'surface',
    '--proffeo-color-surface-inverse',
    'Surface inverse',
    'var(--proffeo-color-base-950)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'surface',
    '--proffeo-color-surface-toolbar',
    'Surface toolbar',
    'var(--proffeo-color-base-300)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'surface',
    '--proffeo-color-overlay-subtle',
    'Overlay subtle',
    'var(--proffeo-color-base-alpha-10)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'surface',
    '--proffeo-color-overlay',
    'Overlay',
    'var(--proffeo-color-base-alpha-30)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'surface',
    '--proffeo-color-overlay-strong',
    'Overlay strong',
    'var(--proffeo-color-base-alpha-50)',
    '--proffeo-seed-base'
  ),

  // Border
  derivedColor('border', '--proffeo-color-border', 'Border', 'var(--proffeo-color-base-200)', '--proffeo-seed-base'),
  derivedColor(
    'border',
    '--proffeo-color-border-strong',
    'Border strong',
    'var(--proffeo-color-border)',
    '--proffeo-color-border'
  ),
  derivedColor(
    'border',
    '--proffeo-color-border-focus',
    'Border focus',
    'var(--proffeo-color-border)',
    '--proffeo-color-border'
  ),
  length('border', '--proffeo-border-width', 'Border width', '1px'),
  length('border', '--proffeo-border-width-strong', 'Border strong width', 'calc(var(--proffeo-border-width) * 2)'),
  length('border', '--proffeo-border-width-focus', 'Border focus width', 'var(--proffeo-border-width-strong)'),

  // Text
  derivedColor('text', '--proffeo-color-text', 'Text', 'var(--proffeo-color-base-800)', '--proffeo-seed-base'),
  derivedColor(
    'text',
    '--proffeo-color-text-muted',
    'Text muted',
    'var(--proffeo-color-base-600)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'text',
    '--proffeo-color-text-disabled',
    'Text disabled',
    'var(--proffeo-color-base-400)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'text',
    '--proffeo-color-text-readonly',
    'Text readonly',
    'var(--proffeo-color-text-muted)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'text',
    '--proffeo-color-text-on-action',
    'Text on action',
    'var(--proffeo-color-base-50)',
    '--proffeo-seed-base'
  ),
  derivedColor('text', '--proffeo-color-icon', 'Icon', 'var(--proffeo-color-base-900)', '--proffeo-seed-base'),

  // Feedback
  hexColor('feedback', '--proffeo-color-danger', 'Danger', '#ef4444'),
  hexColor('feedback', '--proffeo-color-warning', 'Warning', '#efaa41'),
  hexColor('feedback', '--proffeo-color-success', 'Success', '#059669'),
  hexColor('feedback', '--proffeo-color-info', 'Info', '#0ea5e9'),

  // Focus
  derivedColor(
    'focus',
    '--proffeo-focus-ring-color',
    'Focus ring color',
    'var(--proffeo-color-accent-alpha-35)',
    '--proffeo-seed-accent'
  ),
  {
    ...text('focus', '--proffeo-focus-ring', 'Focus ring', '0 0 0 2px var(--proffeo-focus-ring-color)'),
    description: 'Focus ring token derived from --proffeo-focus-ring-color.',
    usage:
      'Change --proffeo-seed-accent to update --proffeo-focus-ring; this derivative token is read-only in the playground.',
    editable: false
  },

  // Status
  rgbaColor('status', '--proffeo-status-in-planning', 'In planning', 'rgba(100, 116, 139, 0.6)'),
  rgbaColor('status', '--proffeo-status-to-do', 'To do', 'rgba(2, 38, 216, 0.6)'),
  rgbaColor('status', '--proffeo-status-in-progress', 'In progress', 'rgba(99, 102, 241, 0.6)'),
  rgbaColor('status', '--proffeo-status-in-review', 'In review', 'rgba(147, 51, 234, 0.7)'),
  rgbaColor('status', '--proffeo-status-accepted', 'Accepted', 'rgba(217, 119, 6, 0.6)'),
  rgbaColor('status', '--proffeo-status-rejected', 'Rejected', 'rgba(239, 68, 68, 0.75)'),
  rgbaColor('status', '--proffeo-status-prod-ready', 'Prod ready', 'rgba(59, 234, 157, 0.65)'),
  rgbaColor('status', '--proffeo-status-done', 'Done', 'rgba(20, 138, 64, 0.9)'),

  // Priority
  hexColor('priority', '--proffeo-priority-low', 'Low', '#64748b'),
  hexColor('priority', '--proffeo-priority-normal', 'Normal', '#64748b'),
  hexColor('priority', '--proffeo-priority-medium', 'Medium', '#fb923c'),
  hexColor('priority', '--proffeo-priority-high', 'High', '#e11d48'),
  hexColor('priority', '--proffeo-priority-critical', 'Critical', '#b91c1c'),

  // Report status
  hexColor('reportStatus', '--proffeo-report-status-unknown', 'Unknown', '#475569'),
  hexColor('reportStatus', '--proffeo-report-status-pending', 'Pending', '#7b7b7b'),
  hexColor('reportStatus', '--proffeo-report-status-approved', 'Approved', '#ec690e'),
  hexColor('reportStatus', '--proffeo-report-status-processed', 'Processed', '#7db64f'),

  // Domain exceptions
  hexColor('domain', '--proffeo-color-file-video', 'File video', '#a855f7'),
  hexColor('domain', '--proffeo-color-role-industry-admin', 'Role industry admin', '#1e40af'),
  hexColor('domain', '--proffeo-color-role-group-admin', 'Role group admin', '#991b1b'),
  hexColor('domain', '--proffeo-color-osh-risk-low', 'OSH risk low', '#25af0a'),
  hexColor('domain', '--proffeo-color-osh-risk-medium', 'OSH risk medium', '#1a6c06'),
  hexColor('domain', '--proffeo-color-osh-risk-high', 'OSH risk high', '#ffa500'),
  hexColor('domain', '--proffeo-color-osh-risk-critical', 'OSH risk critical', '#ff0000'),

  // Radius
  length('radius', '--proffeo-radius-xs', 'Radius XS', '2px'),
  length('radius', '--proffeo-radius-sm', 'Radius SM', '4px'),
  length('radius', '--proffeo-radius-md', 'Radius MD', '6px'),
  length('radius', '--proffeo-radius-lg', 'Radius LG', '8px'),
  length('radius', '--proffeo-radius-xl', 'Radius XL', '12px'),
  length('radius', '--proffeo-radius-full', 'Radius full', '9999px'),
  length('radius', '--proffeo-radius-control', 'Radius control', '8px'),

  // Shadow
  text('shadow', '--proffeo-shadow-none', 'Shadow none', 'none'),
  {
    ...text('shadow', '--proffeo-shadow-card', 'Shadow card', '0 1px 2px var(--proffeo-color-base-alpha-6)'),
    description: 'Shadow card token derived from --proffeo-seed-base.',
    usage:
      'Change --proffeo-seed-base to update --proffeo-shadow-card; this derivative token is read-only in the playground.',
    editable: false
  },
  {
    ...text('shadow', '--proffeo-shadow-dropdown', 'Shadow dropdown', '0 8px 24px var(--proffeo-color-base-alpha-12)'),
    description: 'Shadow dropdown token derived from --proffeo-seed-base.',
    usage:
      'Change --proffeo-seed-base to update --proffeo-shadow-dropdown; this derivative token is read-only in the playground.',
    editable: false
  },
  {
    ...text('shadow', '--proffeo-shadow-dialog', 'Shadow dialog', '0 16px 48px var(--proffeo-color-base-alpha-18)'),
    description: 'Shadow dialog token derived from --proffeo-seed-base.',
    usage:
      'Change --proffeo-seed-base to update --proffeo-shadow-dialog; this derivative token is read-only in the playground.',
    editable: false
  },

  // Spacing
  length('spacing', '--proffeo-space-0', 'Space 0', '0px'),
  length('spacing', '--proffeo-space-1', 'Space 1', '4px'),
  length('spacing', '--proffeo-space-2', 'Space 2', '8px'),
  length('spacing', '--proffeo-space-3', 'Space 3', '12px'),
  length('spacing', '--proffeo-space-4', 'Space 4', '16px'),
  length('spacing', '--proffeo-space-5', 'Space 5', '20px'),
  length('spacing', '--proffeo-space-6', 'Space 6', '24px'),
  length('spacing', '--proffeo-space-8', 'Space 8', '32px'),
  length('spacing', '--proffeo-space-10', 'Space 10', '40px'),
  length('spacing', '--proffeo-space-12', 'Space 12', '48px'),

  // Layout
  length('layout', '--proffeo-layout-content-max-width', 'Content max width', '1536px'),

  // Breakpoints
  length('breakpoint', '--proffeo-breakpoint-sm', 'Breakpoint SM', '600px'),
  length('breakpoint', '--proffeo-breakpoint-md', 'Breakpoint MD', '960px'),
  length('breakpoint', '--proffeo-breakpoint-lg', 'Breakpoint LG', '1280px'),
  length('breakpoint', '--proffeo-breakpoint-xl', 'Breakpoint XL', '1440px'),
  length('breakpoint', '--proffeo-breakpoint-2xl', 'Breakpoint 2XL', '1600px'),

  // Disabled
  num('disabled', '--proffeo-opacity-disabled', 'Opacity disabled', '0.5'),

  // State
  num('state', '--proffeo-state-hover-opacity', 'Hover state opacity', '0.15'),
  num('state', '--proffeo-state-focus-opacity', 'Focus state opacity', '0.12'),
  length('state', '--proffeo-control-state-layer-size', 'Control state layer size', '36px'),

  // Icon
  length('icon', '--proffeo-icon-size-xxs', 'Icon XXS', '12px'),
  length('icon', '--proffeo-icon-size-xs', 'Icon XS', '16px'),
  length('icon', '--proffeo-icon-size-sm', 'Icon SM', '18px'),
  length('icon', '--proffeo-icon-size-md', 'Icon MD', '20px'),
  length('icon', '--proffeo-icon-size-base', 'Icon base', '24px'),
  length('icon', '--proffeo-icon-size-lg', 'Icon LG', '28px'),
  length('icon', '--proffeo-icon-size-xl', 'Icon XL', '32px'),
  length('icon', '--proffeo-icon-size-2xl', 'Icon 2XL', '40px'),
  length('icon', '--proffeo-icon-size-3xl', 'Icon 3XL', '55px'),

  // Scrollbar
  derivedColor(
    'scrollbar',
    '--proffeo-scrollbar-thumb',
    'Thumb',
    'var(--proffeo-color-base-400)',
    '--proffeo-seed-base'
  ),
  derivedColor(
    'scrollbar',
    '--proffeo-scrollbar-track',
    'Track',
    'var(--proffeo-color-base-alpha-6)',
    '--proffeo-seed-base'
  )
];

export const PROFFEO_TOKEN_CSS_VARIABLES: string[] = PROFFEO_TOKEN_EDITOR_FIELDS.map(
  tokenField => tokenField.cssVariable
);
